import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

/**
 * Props for the SafeHTML component
 * @interface SafeHTMLProps
 */
interface SafeHTMLProps {
  /**
   * The raw HTML content to be sanitized and rendered.
   * @type {string}
   */
  htmlContent: string;
}

/**
 * A React component that safely renders HTML content by sanitizing it with DOMPurify
 * and parsing it with html-react-parser.
 *
 * @param {SafeHTMLProps} props - The component props
 * @returns {JSX.Element} The sanitized and parsed HTML content
 * @example
 * ```tsx
 * <SafeHTML htmlContent="<p>Hello <strong>World</strong>!</p>" />
 * ```
 */
export function SafeHTML({ htmlContent }: SafeHTMLProps) {
  // Sanitize the HTML content using DOMPurify
  const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: ['p', 'div', 'span', 'strong', 'em', 'a', 'ul', 'li', 'ol'],
    ALLOWED_ATTR: ['href', 'target', 'class'],
  });

  // render the sanitized html
  return <>{parse(sanitizedHTML)}</>;
}
